<template>
    <div class="page-wrap">
        <div class="page-bar">
            <div>
                <div class="page-bar-sub">Team</div>
                <div class="page-bar-main"><span v-if="$store.state.user.teamHubRole != 'Admin'">My </span>Employees<info-tip v-if="$store.state.user.isAdmin" text="Employee records are Team Hub records and don't always represent users who can sign in to the Ironbook. To add a new user to the Ironbook, add them in Settings > Standard Users first." /></div>
            </div>
        </div>
        <div :class="$store.state.routeLoaded ? 'slide-up view' : 'hide'">
            <div class="page-filters pf-bar">
                <div class="pf-search">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.35-4.35" /></svg>
                    <input type="text" v-model="searchQ" placeholder="Search" aria-label="Search" @input="search()" />
                </div>
                <select v-model="status" @change="search()">
                    <option value="Active">Active</option>
                    <option value="Terminated">Terminated</option>
                </select>
                <select v-if="$store.state.user.isAdmin" v-model="selectedLocation" @change="search()">
                    <option value="ALL">All Locations</option>
                    <option v-for="(loc, i) in $store.state.user.locations" :key="i" :value="loc">{{ loc }}</option>
                </select>
                <select v-if="$store.state.user.companyID == 1" v-model="selectedShift" @change="search()">
                    <option v-for="(o, i) in shiftOptions" :value="o.value" :key="i">{{ o.label }}</option>
                </select>
                <select v-if="$store.state.user.teamHubRole == 'Admin'" v-model="selectedManager" @change="search()">
                    <option :value="null">Reporting To</option>
                    <option v-for="(o, i) in managerEmployees" :value="o.ID" :key="i">{{ o.Name }}</option>
                </select>
                <span class="pf-spacer"></span>
                <div @click="addNewEmployee()" class="add-btn">Add</div>
                <a class="add-btn" :href="`${$store.state.apiUrl}/api/employees/employees.csv`" download>Export</a>
            </div>
            <table class="full-table">
                <tbody>
                    <tr class="table-labels">
                        <td>Name</td>
                        <td>Title</td>
                        <td v-if="$store.state.user.companyID == 1">Role</td>
                        <td v-if="$store.state.user.companyID == 1">Shift</td>
                        <td>Email</td>
                        <td>Phone</td>
                        <td></td>
                    </tr>
                    <tr style="padding:0;height:0"></tr>
                    <tr v-for="(e, i) in employees" :key="i">
                        <td>
                            <a @click="viewEmployee(e)">{{ e.Name }}</a>
                            <div v-if="e.NeedsReview && status == 'Active'" style="margin-top:3px">*needs review</div>
                        </td>
                        <td>{{ e.SeatTitle }}</td>
                        <td v-if="$store.state.user.companyID == 1">{{ e.Role }}</td>
                        <td v-if="$store.state.user.companyID == 1">{{ e.Shift }}</td>
                        <td><a v-if="e.Email" :href="'mailto:'+e.Email" target="_blank">{{ e.Email }}</a></td>
                        <td><a v-if="e.Phone" :href="'tel:'+e.Phone" target="_blank">{{ formatPhone(e.Phone) }}</a></td>
                        <td>
                            <div class="row-menu">
                                <button type="button" class="row-menu-btn" @click.stop="toggleRowMenu(e)">
                                    <svg-icon icon="dots" />
                                </button>
                                <div v-if="openMenuID == e.ID" class="row-menu-dropdown">
                                    <a @click="openMenuID = null; editEmployee(e)">Edit</a>
                                    <a @click="openMenuID = null; reviewEmployee(e)">Review</a>
                                    <a @click="openMenuID = null; deleteEmployee(e)">Delete</a>
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <confirm-delete v-if="showConfirmDelete" @confirm="deleteConfirm" @cancel="() => { showConfirmDelete = false; }" msg="Are you sure you would like to delete this employee?"/>
        <add-edit-employee v-if="showAddEdit" :employeeToEdit="employeeToEdit" @close="() => { showAddEdit = false; getEmployees(); }" />
        <add-employee-review v-if="showReview" :employee="employeeToReview" :employeeReviewFields="employeeReviewFields" @close="() => { showReview = false; getEmployees() }" />
    </div>
</template>

<script lang="ts">
import { defineComponent, registerRuntimeCompiler } from 'vue';
import axios from 'axios';
import AddEditEmployee from '@/components/AddEditEmployee.vue';
import ConfirmDelete from '@/components/ConfirmDelete.vue';
import AddEmployeeReview from '@/components/AddEmployeeReview.vue';
import SvgIcon from '@/components/SvgIcon.vue';
import InfoTip from '@/components/InfoTip.vue';

export default defineComponent({
    name: 'EmployeesView',
    components: {
        AddEditEmployee,
        ConfirmDelete,
        AddEmployeeReview,
        InfoTip,
        SvgIcon
    },
    data() {
        return {
            allEmployees: [] as any,
            employees: [] as any,
            managerEmployees: [] as any,
            selectedManager: null as any,
            selectedLocation: 'ALL',
            searchQ: '',
            disableButton: false,
            employeeToEdit: null as any,
            showAddEdit: false,
            employeeReviewFields: [] as any,
            employeeIDToDelete: null as any,
            showConfirmDelete: false,
            status: 'Active',
            showReview: false,
            employeeToReview: null as any,
            openMenuID: null as any,

            // sf only
            selectedShift: null as any,
            shiftOptions: [] as any
        }
    },
    async created() {
        while(!this.$store.state.employeeFieldsLoaded) {
            await new Promise(resolve => setTimeout(resolve, 200));
        }

        if(this.$route.query.status) {
            this.status = this.$route.query.status;
        }
        if(this.$route.query.location) {
            this.selectedLocation = this.$route.query.location;
        }
        if(this.$route.query.shift) {
            this.selectedShift = this.$route.query.shift;
        }
        if(this.$route.query.manager) {
            this.selectedManager = Number(this.$route.query.manager);
        }
        if(this.$route.query.search) {
            this.searchQ = this.$route.query.search.toLowerCase();
        }

        await this.getEmployees();
        await axios.get(`${this.$store.state.apiUrl}/api/employees/reviewfields`)
            .then(resp => {
                if(resp.data) {
                    this.employeeReviewFields = resp.data;
                }
            })
            .catch(e => {
                console.log(e);
            })

        if(this.$store.state.user.companyID == 1) {
            const shiftField = this.$store.state.employeeFields.find((f: any) => {
                return f.id == 'e95de940-fba7-4b03-8639-1ea23537157b';
            });
            if(shiftField) {
                this.shiftOptions = [
                    {
                        label: 'All Shifts',
                        value: null
                    }
                ]
                for(const op of shiftField.options) {
                    this.shiftOptions.push({
                        label: op,
                        value: op
                    })
                }
            }
        }

        this.$store.state.routeLoaded = true;
    },
    methods: {
        async getEmployees() {
            await axios.get(`${this.$store.state.apiUrl}/api/employees`)
                .then(resp => {
                    const nowTS = new Date().getTime();
                    const needsReviewMS = (1000 * 60 * 60 * 24 * 30);
                    for(const em of resp.data) {
                        let lastTS = new Date(em.CreateDate).getTime();
                        if(em.LastReviewedDate) {
                            lastTS = new Date(em.LastReviewedDate).getTime();
                        }
                        if(nowTS - lastTS > needsReviewMS) {
                            em.NeedsReview = true;
                        }
                    }

                    if(this.$store.state.user.companyID == 1) {
                        for(const em of resp.data) {
                            try {
                                if(em.AdditionalFieldsJSON) {
                                    const additionalFields = JSON.parse(em.AdditionalFieldsJSON);
                                    const startDateField = additionalFields.find((f: any) => {
                                        return f.name == 'Start Date';
                                    });
                                    if(startDateField?.value) {
                                        const dateSplit = startDateField.value.split('-');
                                        em.StartDate = `${parseInt(dateSplit[1])}/${parseInt(dateSplit[2])}/${parseInt(dateSplit[0])}`
                                    }
                                    const roleField = additionalFields.find((f: any) => {
                                        return f.id == '1c6cbe7c-fe83-47a2-950a-c405ed67bbd8';
                                    });
                                    if(roleField?.value) {
                                        em.Role = roleField.value;
                                    }
                                    const shiftField = additionalFields.find((f: any) => {
                                        return f.id == 'e95de940-fba7-4b03-8639-1ea23537157b';
                                    });
                                    if(shiftField?.value) {
                                        em.Shift = shiftField.value;
                                    }
                                }
                            } catch(e) {
                                // nothing
                            }
                        }
                    }
                    this.allEmployees = resp.data;
                    this.managerEmployees = resp.data.filter((e: any) => {
                        return e.IsManager;
                    })
                })
                .catch(e => {
                    console.log(e);
                })
            this.search();
        },
        search() {
            let employeesTemp = [];
            employeesTemp = this.allEmployees.filter((em: any) => {
                return em.Status == this.status;
            })
            if(this.selectedLocation != 'ALL') {
                employeesTemp = employeesTemp.filter((e: any) => {
                    return e.Location == this.selectedLocation;
                })
            }
            if(this.selectedShift) {
                employeesTemp = employeesTemp.filter((e: any) => {
                    return e.Shift == this.selectedShift;
                })
            }
            if(this.selectedManager) {
                employeesTemp = employeesTemp.filter((e: any) => {
                    return e.ReportingTo == this.selectedManager;
                })
            }
            const query = {} as any;
            query.status = this.status;
            if(this.$store.state.user.isAdmin) {
                query.location = this.selectedLocation;
            }
            if(this.selectedShift) {
                query.shift = this.selectedShift;
            }
            if(this.selectedManager) {
                query.manager = this.selectedManager;
            }

            if(this.searchQ.length > 3) {
                const searchQLower = this.searchQ.toLowerCase();
                query.search = searchQLower;
                employeesTemp = employeesTemp.filter((e: any) => {
                    return e.Name.toLowerCase().includes(searchQLower);
                })
            }

            this.$router.replace({
                path: this.$route.path,
                query: query
            }).catch((e: any) => {
                console.log(e);
            });
            this.employees = employeesTemp;
        },
        addNewEmployee() {
            this.employeeToEdit = null;
            this.showAddEdit = true;
        },
        editEmployee(e: any) {
            this.employeeToEdit = e;
            this.showAddEdit = true;
        },
        reviewEmployee(e: any) {
            this.employeeToReview = e;
            this.showReview = true;
        },
        viewEmployee(e: any) {
            this.$router.push(`/employee/${e.ID}`);
        },
        deleteEmployee(e: any) {
            this.employeeIDToDelete = e.ID;
            this.showConfirmDelete = true;
        },
        deleteConfirm() {
            this.showConfirmDelete = false;
            axios.delete(`${this.$store.state.apiUrl}/api/employees/${this.employeeIDToDelete}`)
                .then(resp => {
                    // Their public user was deactivated; an admin account has to be removed by hand
                    if(resp.data?.warning) {
                        alert(resp.data.warning);
                    }
                    this.getEmployees();
                })
                .catch(e => {
                    alert('There was an error')
                    console.log(e);
                })
        },
        toggleRowMenu(e: any) {
            this.openMenuID = this.openMenuID == e.ID ? null : e.ID;
        },
        handleOutsideRowMenuClick(e: MouseEvent) {
            if(this.openMenuID && !(e.target as HTMLElement).closest('.row-menu')) {
                this.openMenuID = null;
            }
        }
    },
    mounted() {
        document.addEventListener('click', this.handleOutsideRowMenuClick);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleOutsideRowMenuClick);
    }
});
</script>
